import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { liveCollection, materials } from '../../data/collectes';
import { resetLiveTruck, useLiveTruck } from '../../hooks/useLiveTruck';
import { TruckPreviewMap } from '../collectes/TruckPreviewMap';
interface FormAtnaHintProps {
  /** Type de demande choisi au 2e niveau (ex. « Bac non ramassé »). */
  type: string;
}
const ease = [0.23, 1, 0.32, 1] as const;

/**
 * Indice contextuel d'ATNA dans le formulaire manuel : ATNA n'intervient pas dans la saisie,
 * elle ajoute seulement une information utile selon le type choisi.
 */
export function FormAtnaHint({
  type
}: FormAtnaHintProps) {
  if (type === 'Bac non ramassé') return <BinHint data-id="element-1189" data-mp="2xh7jx0d969kc" />;
  if (type === 'Lampadaire éteint' || type === 'Lampadaire qui clignote') {
    return <HintFrame data-id="element-1190" data-mp="32gkhog5nx11p">
        <p className="text-sm font-bold text-ville-navy" data-id="element-1191" data-mp="32ezl6kzsf817">Vous avez déjà une demande ouverte pour un lampadaire.</p>
        <p className="mt-1 text-sm text-slate-600" data-id="element-1192" data-mp="1czmf3zew5i42">
          #REQ-2790 · 4610, rue Bourgogne est en traitement. S’il s’agit du même lampadaire, inutile d’en créer une
          nouvelle.
        </p>
        <Link to="/mes-demandes/req-2790" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1193" data-mp="1mpfkgiofx0zv">
          Voir la demande
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1194" data-mp="q19ik3iwkmpa" />
        </Link>
      </HintFrame>;
  }
  return null;
}

/** Indice affiché à côté des photos : pour un nid-de-poule, la photo sert à estimer la taille. */
export function PhotoAtnaHint({
  type
}: FormAtnaHintProps) {
  if (type !== 'Nid-de-poule') return null;
  return <HintFrame className="min-w-0 flex-1 sm:min-w-[260px]" data-id="element-1195" data-mp="2fdl2uys0udwj">
      <p className="text-sm font-bold text-ville-navy" data-id="element-1196" data-mp="2ah4fi6snc3ki">Ajoutez une photo pour accélérer l’intervention.</p>
      <p className="mt-1 text-sm text-slate-600" data-id="element-1197" data-mp="2ifzbmt6ecn83">
        Elle permet d’estimer la taille du nid-de-poule et de prioriser la réparation.
      </p>
    </HintFrame>;
}
function BinHint() {
  const {
    minutes,
    arrived
  } = useLiveTruck();
  const materialLabel = materials[liveCollection.material].label;

  // La carte apparaît avec le camion à son point de départ.
  useEffect(() => {
    resetLiveTruck();
  }, []);
  return <HintFrame data-id="element-1198" data-mp="1yumjqe46bi07">
      <p className="text-sm font-bold text-ville-navy" data-id="element-1199" data-mp="1yqypfcqwmrkf">
        {arrived ? 'Le camion vient de passer à votre adresse.' : 'Le camion n’est pas encore passé chez vous.'}
      </p>
      <p className="mt-1 text-sm text-slate-600" data-id="element-1200" data-mp="28p4q5r0z5rnp">
        {arrived ? 'Si votre bac est toujours plein, continuez votre demande : un rattrapage sera planifié sous 48 heures ouvrables.' : `Collecte ${materialLabel.toLowerCase()} en cours · ${liveCollection.truck} · arrivée dans ${minutes} minute${minutes > 1 ? 's' : ''}. Laissez votre bac au bord de la rue.`}
      </p>
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200" data-id="element-1201" data-mp="18j177vx6wdzy">
        <TruckPreviewMap data-id="element-1202" data-mp="18gy6ohyev2gz" />
      </div>
    </HintFrame>;
}
function HintFrame({
  children,
  className = 'mt-5'
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.aside aria-live="polite" initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.22,
    ease
  }} className={`flex gap-3 rounded-2xl bg-ville-indigo-soft p-4 ${className}`} data-id="element-1203" data-mp="ykbnrypnalhl">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-ville-indigo" aria-hidden="true" data-id="element-1204" data-mp="kt6vxisiqxht">
        <SparklesIcon className="h-4 w-4" data-id="element-1205" data-mp="fvx11nmd1tml" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-1206" data-mp="avshfoq5ois5">
        <p className="text-xs font-bold text-ville-indigo" data-id="element-1207" data-mp="aur57gxelq8u">ATNA</p>
        <div className="mt-0.5" data-id="element-1208" data-mp="6tfbaz7hyiuu">{children}</div>
      </div>
    </motion.aside>;
}